import { Clear, TouchApp, LocalShipping } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { alpha, Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormHelperText, Grid, IconButton, OutlinedInput, Switch, Typography } from '@mui/material'
import { cloneDeep, get, mapValues, pick, set } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IncreaseDecreaseInput from '../../../components/IncreaseDecreaseInput'
import { runJoiValidate } from 'src/_utils'
import { SHIPMENT_BOX_SCHEMA } from '../../../validationSchemas'
import { useCreateBoxMutation, useUpdateBoxMutation, useDeleteBoxMutation } from 'src/_api/shipmentBox'

export default function BoxDialog({
    open,
    onClose,
    box,
    isEditing
}) {

    const { t } = useTranslation()

    const [createBox, {
        isLoading: createBoxLoading,
        isSuccess: createBoxIsSuccess,
        error: createError,
        reset: resetCreateBox
    }] = useCreateBoxMutation()

    const [updateBox, {
        isLoading: updateBoxLoading,
        isSuccess: updateBoxIsSuccess,
        error: updateBoxError,
        reset: resetUpdateBox
    }] = useUpdateBoxMutation()

    const [deleteBox, {
        isLoading: deleteBoxLoading,
        isSuccess: deleteBoxIsSuccess,
        error: deleteBoxError,
        reset: resetDeleteBox
    }] = useDeleteBoxMutation()

    const [tempBox, setTempBox] = useState({
        boxName: "",
        weight: '1',
        dimensions: {
            length: '',
            width: '',
            height: '',
        },
        default: false,
        thirdPartyDefault: false
    })

    useEffect(() => {
        if (!!box && isEditing) {
            setTempBox(pick(box, ['boxName', 'dimensions', 'default', 'thirdPartyDefault', 'weight']))
        }
    }, [box, isEditing])

    const handleChange = ({ target: { name, value } }) => {
        setTempBox((prevState) => {
            let payload = cloneDeep(prevState)
            set(payload, name, value)
            return payload
        })
    }

    const [validationErrors, setValidationErrors] = useState(null)

    const onSubmit = () => {
        try {
            setValidationErrors(null)

            let payload = cloneDeep(tempBox)

            // Convert dimensions and weight to numbers
            payload.dimensions = mapValues(payload.dimensions, Number);

            if (!!payload.weight) {
                payload.weight = Number(payload.weight);
            }

            let errors = runJoiValidate(SHIPMENT_BOX_SCHEMA, payload)

            console.log("tempBox", payload)
            console.log("errors", errors)

            if (!!errors) {
                setValidationErrors(errors)
                return
            }

            if (isEditing) {
                updateBox({ id: box.id, payload }).unwrap()
            } else {
                createBox(payload).unwrap()
            }

            setTimeout(() => {
                onClose()
            }, 3000)

        } catch (error) {
            console.log(error)
        }
    }

    const onDelete = async () => {
        try {
            await deleteBox(box.id).unwrap()

            setTimeout(() => {
                onClose()
            }, 3000)

        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        if (!open) {
            setTempBox({
                boxName: "",
                weight: '1',
                dimensions: {
                    length: '',
                    width: '',
                    height: '',
                },
                default: false,
                thirdPartyDefault: false
            })
            resetCreateBox()
            resetUpdateBox()
            resetDeleteBox()
            setValidationErrors(null)
        }
    }, [open])

    const loading = createBoxLoading || updateBoxLoading || deleteBoxLoading
    const isSuccess = createBoxIsSuccess || updateBoxIsSuccess || deleteBoxIsSuccess
    const error = createError || updateBoxError || deleteBoxError

    return (
        <Dialog
            open={open}
            fullWidth
            onClose={(e) => e.preventDefault()}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
                {isEditing ? t("Edit Box") : t("Add Box")}
                <IconButton sx={{ ml: "auto" }} onClick={onClose} color="primary">
                    <Clear />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" flexDirection="column">
                            <Typography variant="subtitle1">{t("Box Name")} <span style={{ color: "red" }}>*</span></Typography>
                            <OutlinedInput
                                value={tempBox?.boxName}
                                onChange={handleChange}
                                name="boxName"
                                size="small"
                                error={!!validationErrors?.boxName}
                            />
                            <FormHelperText error={!!validationErrors?.boxName}>
                                {validationErrors?.boxName ?? ''}
                            </FormHelperText>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <IncreaseDecreaseInput
                            decimal={false}
                            max={999999}
                            adornment={t("cm")}
                            label={t("Length")}
                            labelColor="red"
                            value={tempBox?.dimensions?.length}
                            onChange={handleChange}
                            name="dimensions.length"
                            error={get(validationErrors, 'dimensions.length')}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <IncreaseDecreaseInput
                            decimal={false}
                            max={999999}
                            adornment={t("cm")}
                            label={t("Width")}
                            labelColor="red"
                            value={tempBox?.dimensions?.width}
                            onChange={handleChange}
                            name="dimensions.width"
                            error={get(validationErrors, 'dimensions.width')}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <IncreaseDecreaseInput
                            decimal={false}
                            max={999999}
                            adornment={t("cm")}
                            label={t("Height")}
                            labelColor="red"
                            value={tempBox?.dimensions?.height}
                            onChange={handleChange}
                            name="dimensions.height"
                            error={get(validationErrors, 'dimensions.height')}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <IncreaseDecreaseInput
                            decimal={true}
                            max={999999}
                            adornment={t("kg")}
                            label={t("Weight")}
                            labelColor="red"
                            value={tempBox?.weight}
                            onChange={handleChange}
                            name="weight"
                            error={get(validationErrors, 'weight')}
                            fullWidth
                        />
                    </Grid>
                </Grid>
                {/* Default Behavior Settings */}
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                        {t("Default Behavior")}
                    </Typography>

                    {/* Pre-selected Box Switch */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: tempBox?.default ? 'primary.main' : 'divider',
                            bgcolor: (theme) => tempBox?.default ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                            <TouchApp sx={{ color: tempBox?.default ? 'primary.main' : 'action.active', mt: 0.25 }} />
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {t("Pre-selected Box")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                    {t("This box will appear as the default selection when creating orders manually. You can still change it before confirming.")}
                                </Typography>
                            </Box>
                        </Box>
                        <Switch
                            checked={!!tempBox?.default}
                            onChange={({ target: { checked } }) => handleChange({ target: { name: "default", value: checked } })}
                            sx={{ ml: 1, flexShrink: 0 }}
                        />
                    </Box>

                    {/* Auto-assign to Orders Switch */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: tempBox?.thirdPartyDefault ? 'primary.main' : 'divider',
                            bgcolor: (theme) => tempBox?.thirdPartyDefault ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                            <LocalShipping sx={{ color: tempBox?.thirdPartyDefault ? 'primary.main' : 'action.active', mt: 0.25 }} />
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {t("Auto-assign to Orders")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                    {t("Automatically add this box to orders from external platforms (Salla, Zid, Shopify) that don't include box information.")}
                                </Typography>
                            </Box>
                        </Box>
                        <Switch
                            checked={!!tempBox?.thirdPartyDefault}
                            onChange={({ target: { checked } }) => handleChange({ target: { name: "thirdPartyDefault", value: checked } })}
                            sx={{ ml: 1, flexShrink: 0 }}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <Divider />
            <DialogActions>
                {isEditing && (
                    <LoadingButton
                        type="button"
                        loading={loading}
                        onClick={onDelete}
                        color="error"
                        variant="contained"
                        sx={{ textTransform: "none" }}
                    >
                        {t("Delete")}
                    </LoadingButton>
                )}
                <Box flex={1} textAlign="center">
                    {!!error && <Typography color="error">{error}</Typography>}
                    {isSuccess && <Typography color="success.main">{t("Done successfully")}</Typography>}
                </Box>
                <Box mr="auto" />
                <LoadingButton
                    type="button"
                    loading={loading}
                    onClick={onClose}
                    color="primary"
                    variant="contained"
                    sx={{ textTransform: "none" }}
                >
                    {t("Cancel")}
                </LoadingButton>
                <LoadingButton
                    type="button"
                    loading={loading}
                    onClick={onSubmit}
                    color="primary"
                    variant="contained"
                    sx={{ textTransform: "none" }}
                >
                    {isEditing ? t("Update") : t("Add")}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}